// Consumes a Supabase Auth "recovery"/"invite" link -- lands here as
// `#access_token=...&refresh_token=...&type=recovery` on whatever page
// the project's Auth "Site URL" is configured to redirect to (currently
// the app root). Checked for at the very top of AppLayout, before the
// normal currentUser/isSuperadmin routing, so it works regardless of
// which path the redirect actually lands on.
//
// This is the only place in the app that calls supabase.auth.updateUser
// with a real password -- and it's always the PERSON THEMSELVES typing
// it into their own browser, directly into Supabase's own auth call.
// Nothing here (or anywhere upstream: AppContext, edge functions) ever
// sees or stores the plaintext value.
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

interface ResetPasswordProps {
  /** Called once the password's been set and it's safe to drop back into the normal app. */
  onDone: () => void;
}

// The recovery link's session (established via setSession above) is only
// ever meant to authorize this one password change -- signed out before
// handing back to the normal app so the person always goes through a
// real login (driver/admin/superadmin, whichever applies) afterward,
// rather than being left half-logged-in from a session AppContext never
// asked for and doesn't track.
const returnToLogin = async (onDone: () => void) => {
  await supabase.auth.signOut();
  onDone();
};

const ResetPassword: React.FC<ResetPasswordProps> = ({ onDone }) => {
  const [establishing, setEstablishing] = useState(true);
  const [establishError, setEstablishError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const accessToken = hash.get('access_token');
    const refreshToken = hash.get('refresh_token');

    if (!accessToken || !refreshToken) {
      setEstablishError('This link is missing or malformed. Ask for a fresh password recovery email and try again.');
      setEstablishing(false);
      return;
    }

    supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ error }) => {
      // Clear the token out of the URL either way -- it's single-use and
      // shouldn't linger in browser history/address bar regardless of
      // whether it worked.
      window.history.replaceState(null, '', window.location.pathname);
      if (error) {
        setEstablishError('This link has expired or already been used. Ask for a fresh password recovery email and try again.');
      }
      setEstablishing(false);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords don’t match.');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (error) {
      setSubmitError(error.message || 'Failed to update password.');
      return;
    }
    setDone(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center login-container p-4">
      <Card className="w-full max-w-md login-card">
        <CardHeader className="text-center pb-8">
          <CardTitle className="text-2xl font-bold golden-text mb-2">Set Your Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {establishing ? (
            <p className="text-center text-sm text-muted-foreground">Verifying your link...</p>
          ) : establishError ? (
            <>
              <p className="text-center text-red-500 font-medium">{establishError}</p>
              <Button className="w-full golden-button h-12 text-lg font-semibold" onClick={() => returnToLogin(onDone)}>
                Back to login
              </Button>
            </>
          ) : done ? (
            <>
              <p className="text-center text-green-500 font-medium">Password updated. You can log in now.</p>
              <Button className="w-full golden-button h-12 text-lg font-semibold" onClick={() => returnToLogin(onDone)}>
                Continue to login
              </Button>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="newPassword" className="text-yellow-400 font-medium mb-2 block">
                  New password:
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="dark-input h-12 text-lg"
                  autoFocus
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-yellow-400 font-medium mb-2 block">
                  Confirm password:
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="dark-input h-12 text-lg"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full golden-button h-12 text-lg font-semibold"
                disabled={!password || !confirmPassword || submitting}
              >
                {submitting ? 'Saving...' : 'Set Password'}
              </Button>
              {submitError && <p className="text-center text-red-500 font-medium">{submitError}</p>}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
